import React, { useState } from 'react';
import { motion } from 'framer-motion';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';
import { Send, Mail, MapPin, Phone, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const ContactPage = () => {
  const [formData, setFormData] = useState({ name: '', email: '', company: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }
    setLoading(true);
    // Simulate submission
    await new Promise((r) => setTimeout(r, 1200));
    setLoading(false);
    setSubmitted(true);
    toast.success('Your message has been sent!');
  };

  return (
    <div className="bg-[#050505] min-h-screen text-white" data-testid="contact-page">
      <PublicNav />

      <section className="pt-32 pb-24 lg:pb-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center min-h-[70vh]">
            {/* Left — Info */}
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 mb-4">Get in Touch</p>
              <h1
                className="text-5xl md:text-6xl font-medium tracking-tighter leading-[1.1] mb-6"
                style={{ fontFamily: "'Clash Display', sans-serif" }}
              >
                Let's talk about{' '}
                <span className="bg-gradient-to-r from-[#FF3366] via-[#FF8C00] to-[#FFD700] bg-clip-text text-transparent">
                  your needs
                </span>
              </h1>
              <p className="text-lg text-zinc-400 leading-relaxed mb-10" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Whether you want a personalized demo, have questions about pricing, or need help with integration — our team is ready to help.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#FF3366]/10 flex items-center justify-center flex-shrink-0">
                    <Mail size={20} className="text-[#FF3366]" />
                  </div>
                  <div>
                    <p className="text-sm text-zinc-500">Email us at</p>
                    <p className="text-white font-medium">hello@callscriptai.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#FF8C00]/10 flex items-center justify-center flex-shrink-0">
                    <Phone size={20} className="text-[#FF8C00]" />
                  </div>
                  <div>
                    <p className="text-sm text-zinc-500">Call us</p>
                    <p className="text-white font-medium">+1 (800) 555-0199</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#FFD700]/10 flex items-center justify-center flex-shrink-0">
                    <MapPin size={20} className="text-[#FFD700]" />
                  </div>
                  <div>
                    <p className="text-sm text-zinc-500">Office</p>
                    <p className="text-white font-medium">San Francisco, CA</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right — Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
            >
              <div className="rounded-3xl bg-white/5 backdrop-blur-2xl border border-white/10 p-8 lg:p-10">
                {submitted ? (
                  <div className="text-center py-12" data-testid="contact-success">
                    <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 size={36} className="text-emerald-400" />
                    </div>
                    <h3 className="text-2xl font-semibold text-white mb-2">Message Sent!</h3>
                    <p className="text-zinc-400 mb-6">
                      We'll get back to you within 24 hours. In the meantime, feel free to explore our platform.
                    </p>
                    <button
                      onClick={() => { setSubmitted(false); setFormData({ name: '', email: '', company: '', message: '' }); }}
                      className="text-sm text-[#FF3366] font-semibold hover:text-[#FF8C00] transition-colors"
                      data-testid="send-another-btn"
                    >
                      Send another message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                    <h3 className="text-xl font-semibold text-white mb-2">Request a Demo</h3>
                    <p className="text-sm text-zinc-500 mb-4">Fill in the form and we'll schedule a personalized walkthrough.</p>

                    <div>
                      <label className="block text-xs font-bold tracking-[0.15em] uppercase text-zinc-500 mb-2">
                        Name <span className="text-[#FF3366]">*</span>
                      </label>
                      <input
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Your full name"
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-zinc-600 focus:ring-2 focus:ring-[#FF3366] focus:border-transparent transition-all outline-none"
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold tracking-[0.15em] uppercase text-zinc-500 mb-2">
                        Email <span className="text-[#FF3366]">*</span>
                      </label>
                      <input
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="you@company.com"
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-zinc-600 focus:ring-2 focus:ring-[#FF3366] focus:border-transparent transition-all outline-none"
                        data-testid="contact-email-input"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold tracking-[0.15em] uppercase text-zinc-500 mb-2">
                        Company
                      </label>
                      <input
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Your company name"
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-zinc-600 focus:ring-2 focus:ring-[#FF3366] focus:border-transparent transition-all outline-none"
                        data-testid="contact-company-input"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold tracking-[0.15em] uppercase text-zinc-500 mb-2">
                        Message <span className="text-[#FF3366]">*</span>
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us about your use case..."
                        rows={4}
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-zinc-600 focus:ring-2 focus:ring-[#FF3366] focus:border-transparent transition-all outline-none resize-none"
                        data-testid="contact-message-input"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full rounded-full bg-gradient-to-r from-[#FF3366] to-[#FF8C00] text-white py-3.5 font-semibold hover:scale-[1.02] transition-transform shadow-[0_0_20px_rgba(255,51,102,0.3)] flex items-center justify-center gap-2 disabled:opacity-70"
                      data-testid="contact-submit-button"
                    >
                      {loading ? (
                        <Loader2 size={18} className="animate-spin" />
                      ) : (
                        <Send size={18} />
                      )}
                      {loading ? 'Sending...' : 'Send Message'}
                    </button>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default ContactPage;
